import { CompanyContactType, MerchType } from '@api';
import { CompanyContactSelect, FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextareaField, TextField } from '@hypeit-gmbh/hypeit-ui';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useUpdateVenueMerch,
  useVenueMerchFormSchema,
  VenueMerchFormDataType,
} from './hooks';

const TRANS_PATH = 'VENUE_MERCH.FORM';

type VenueMerchFormPropsType = {
  venueId: string;
  overviewVersion: string;
  venueMerch: MerchType;
  onClose: () => void;
  contacts: CompanyContactType[];
};

export const VenueMerchForm: React.FC<VenueMerchFormPropsType> = ({
  venueId,
  overviewVersion,
  venueMerch,
  onClose,
  contacts,
}) => {
  const { t } = useTranslation('venueDetailsOverview');

  const { schema, defaultValues } = useVenueMerchFormSchema(venueMerch);
  const { updateVenueMerch } = useUpdateVenueMerch({
    venueId,
    overviewVersion,
    onClose,
  });

  const methods = useForm<VenueMerchFormDataType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleSubmit = async (data: VenueMerchFormDataType) => {
    await updateVenueMerch(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <TextField<VenueMerchFormDataType>
            fieldName="rate"
            label={t(`${TRANS_PATH}.RATE_LABEL`)}
          />
          <CompanyContactSelect<VenueMerchFormDataType>
            fieldName="contact"
            data={contacts}
            clearable
            searchable
          />
          <TextareaField<VenueMerchFormDataType>
            fieldName="notes"
            label={t(`${TRANS_PATH}.NOTES_LABEL`)}
          />
          <FormActions
            onCancel={onClose}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
